
import React from 'react';
import { motion } from 'framer-motion';
import { UserCheck, MessageCircle, BarChart2, CheckCircle, ArrowRightCircle, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';

const HowItWorksPage = () => {
  const fadeIn = (delay = 0, y = 20) => ({
    hidden: { opacity: 0, y: y, scale: 0.98 },
    visible: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.7, delay, ease: [0.6, -0.05, 0.01, 0.99] } },
  });

  const steps = [
    {
      icon: <UserCheck size={32} sm:size={48} className="text-cm-pink" />,
      title: "Stap 1: Kies je Buddy",
      description: "Elk kind start met het kiezen van een AI-buddy. Deze buddy's zijn speciaal ontworpen voor verschillende thema's zoals vriendschap, spanning of zelfvertrouwen. Een persoonlijke match voor elk kind!",
      imgReplace: <img  alt="Kind kiest een AI buddy op een tablet" className="w-full h-32 sm:h-40 lg:h-48 object-contain rounded-lg mb-4 sm:mb-6 transform group-hover:scale-105 transition-transform duration-300" src="https://storage.googleapis.com/hostinger-horizons-assets-prod/e926ce51-6e11-4ab9-9fdf-60980d54863c/80197076150e4088a6c4a1e1e1859024.png" />
    },
    {
      icon: <MessageCircle size={32} sm:size={48} className="text-cm-yellow" />,
      title: "Stap 2: Praat & Leer",
      description: "Vervolgens kan het kind chatten of interactieve oefeningen doen met de gekozen buddy. Dit alles gebeurt in een 100% veilige, anonieme en oordeelvrije omgeving. Leren en delen was nog nooit zo makkelijk.",
      imgReplace: <img  alt="Kind chat met AI buddy Appie" className="w-full h-32 sm:h-40 lg:h-48 object-contain rounded-lg mb-4 sm:mb-6 transform group-hover:scale-105 transition-transform duration-300" src="https://storage.googleapis.com/hostinger-horizons-assets-prod/e926ce51-6e11-4ab9-9fdf-60980d54863c/872b4f181e90025a798379a75dd3a94b.png" />
    },
    {
      icon: <BarChart2 size={32} sm:size={48} className="text-cm-green" />,
      title: "Stap 3: Inzicht voor School",
      description: "Terwijl kinderen individueel ondersteund worden, krijgt de school waardevolle, geanonimiseerde inzichten in de sociaal-emotionele thema's die spelen. Dit helpt bij het creëren van een nóg betere leeromgeving.",
      imgReplace: <img  alt="Grafiek met inzichten voor scholen" className="w-full h-32 sm:h-40 lg:h-48 object-contain rounded-lg mb-4 sm:mb-6 transform group-hover:scale-105 transition-transform duration-300" src="https://storage.googleapis.com/hostinger-horizons-assets-prod/e926ce51-6e11-4ab9-9fdf-60980d54863c/a6360a69c770e542eb0a44f1486723c9.png" />
    },
    {
      icon: <Users size={32} sm:size={48} className="text-cm-blue" />,
      title: "Stap 4: Groei als Team & Kind",
      description: "Naast de app biedt Classbuddy praktische trainingen voor zowel kinderen als schoolteams. Van weerbaarheid en pesten tot signaleren en gespreksvaardigheid. Zo werk je als school aan een krachtig en duurzaam welzijnsbeleid.",
      imgReplace: <img  alt="Trainer geeft les over pesten aan kinderen in een klaslokaal" className="w-full h-32 sm:h-40 lg:h-48 object-contain rounded-lg mb-4 sm:mb-6 transform group-hover:scale-105 transition-transform duration-300" src="/lovable-uploads/90f7a28c-69c9-477c-a265-c52e36689704.png" />
    }
  ];

  return (
    <div className="container mx-auto px-4 section-padding overflow-hidden">
      <motion.div
        initial="hidden"
        animate="visible"
        className="text-center mb-12 sm:mb-16"
      >
        <motion.h1
          variants={fadeIn(0, 30)}
          className="page-title px-2"
        >
          In <span className="text-gradient-pink-blue font-medium">4 krachtige stappen</span> naar een buddy die luistert, leert en versterkt.
        </motion.h1>
        <motion.p
          variants={fadeIn(0.2, 20)}
          className="page-subtitle px-2"
        >
          Kinderen praten écht met hun eigen digitale buddy:  over vriendschap, pesten, spanning, thuissituaties, keuzes of verdriet. Anoniem, veilig en altijd beschikbaar.<br />
          Ondertussen krijgen scholen inzicht in wat speelt én versterken ze hun aanpak met trainingen voor kinderen én teams.<br />
          Zo bouwen we samen aan meer veerkracht in én buiten het klaslokaal.
        </motion.p>
      </motion.div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8 lg:gap-10">
        {steps.map((step, index) => (
          <motion.div
            key={index}
            variants={fadeIn(0.4 + index * 0.2, 20)}
            initial="hidden"
            animate="visible"
            className={`glassmorphism-card p-6 sm:p-8 rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 flex flex-col items-center text-center h-full group ${
              index === 3 ? 'ring-2 ring-cm-green/30 bg-gradient-to-br from-white/60 to-cm-green-light/20' : ''
            }`}
            whileHover={{ y: -10, scale: 1.02 }}
          >
            <div className="mb-6 sm:mb-8 flex justify-center items-center w-16 h-16 sm:w-20 sm:h-20 lg:w-24 lg:h-24 bg-gradient-to-br from-cm-blue-light to-white rounded-full shadow-lg">
              {step.icon}
            </div>
            {step.imgReplace}
            <h2 className="text-xl sm:text-2xl lg:text-3xl font-sans font-medium text-cm-blue-dark mb-3 sm:mb-4">{step.title}</h2>
            <p className="text-sm sm:text-base text-cm-gray leading-relaxed flex-grow">{step.description}</p>
          </motion.div>
        ))}
      </div>

      <motion.div
        variants={fadeIn(1.0, 20)}
        initial="hidden"
        animate="visible"
        className="mt-12 sm:mt-16 lg:mt-20 p-6 sm:p-8 lg:p-10 bg-gradient-to-r from-cm-green-light/70 via-cm-yellow-light/70 to-cm-pink-light/70 rounded-2xl sm:rounded-3xl shadow-2xl text-center mx-2 sm:mx-0"
      >
        <CheckCircle size={40} sm:size={56} className="text-cm-green mx-auto mb-4 sm:mb-6" />
        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-sans font-medium text-cm-blue-dark mb-4 sm:mb-5">Klaar voor de volgende stap?</h2>
        <p className="text-base sm:text-lg text-cm-gray mb-6 sm:mb-8 max-w-2xl mx-auto leading-relaxed">
          Ontdek zelf hoe Classbuddy het welzijn van kinderen kan transformeren, zowel thuis als op school. Een kleine stap voor een kind, een grote sprong voor hun ontwikkeling.
        </p>
        <Button asChild size="lg" className="bg-gradient-to-r from-cm-pink to-cm-blue hover:from-cm-pink-dark hover:to-cm-blue-dark text-white font-medium text-base sm:text-lg px-6 sm:px-10 py-6 sm:py-7 rounded-xl shadow-xl transform hover:scale-105 transition-all duration-300 group min-h-[48px] w-full sm:w-auto">
          <Link to="/contact">
            Neem Contact Op <ArrowRightCircle className="ml-2 sm:ml-2.5 h-4 w-4 sm:h-5 sm:w-5 group-hover:translate-x-1 transition-transform" />
          </Link>
        </Button>
      </motion.div>
    </div>
  );
};

export default HowItWorksPage;
